// frontend/src/firebase.ts
//
// White-label per-partner Firebase (F2): Firebase is initialised LAZILY, once the
// partner has been resolved (BrandingProvider), with that partner's public web
// config — NOT statically at module load. Consumers must gate on
// `useBranding().firebaseReady` and read `getFirebaseAuth()` only after it is true
// (a pre-init call throws by design). Verity's own hosts init with the Verity
// config, so their behaviour is unchanged.
import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  connectAuthEmulator,
  GoogleAuthProvider,
  signInWithCredential,
  signOut,
  type Auth,
} from "firebase/auth";
import type { FirebaseWebConfig } from "@verity/shared-types";

let app: FirebaseApp | null = null;
let authInstance: Auth | null = null;

/**
 * Initialise Firebase ONCE with the resolved config. Idempotent (a second call
 * returns the existing instance). Throws on a malformed config — the caller
 * (BrandingProvider) catches and degrades to the Verity default rather than
 * white-screening.
 */
export function initFirebase(config: FirebaseWebConfig): Auth {
  if (authInstance) return authInstance;
  app = initializeApp({
    apiKey: config.apiKey,
    authDomain: config.authDomain,
    projectId: config.projectId,
    appId: config.appId,
  });
  authInstance = getAuth(app);
  if (import.meta.env.DEV) connectDevAuthEmulator(authInstance);
  return authInstance;
}

/** What `scripts/verify/invite-link-redemption.mjs` drives from the page. */
export type DevAuthEmulatorHook = {
  host: string;
  /** Sign in as a Google user with an emulator-minted credential (a JSON id token). */
  signInWithGoogleIdToken: (idToken: string) => Promise<void>;
  signOut: () => Promise<void>;
};

/**
 * DEV ONLY: point the Auth SDK at the local Firebase Auth emulator when
 * `VITE_FIREBASE_AUTH_EMULATOR_HOST` is set (`make auth-emulator-up`, see
 * docs/guides/e2e-testing.md), and expose the two calls the e-mail-link
 * redemption gate needs on `window.__verityAuthEmulator`.
 *
 * The only call site sits behind `import.meta.env.DEV`, which Vite replaces
 * with `false` in every `vite build`, so neither the emulator connection nor
 * the window hook exists in a deployed bundle. Without the env var the dev
 * server behaves exactly as before.
 */
function connectDevAuthEmulator(auth: Auth): void {
  const host = import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST;
  if (!host) return;
  connectAuthEmulator(auth, `http://${host}`, { disableWarnings: true });
  const hook: DevAuthEmulatorHook = {
    host,
    signInWithGoogleIdToken: async (idToken) => {
      await signInWithCredential(auth, GoogleAuthProvider.credential(idToken));
    },
    signOut: () => signOut(auth),
  };
  (window as Window & { __verityAuthEmulator?: DevAuthEmulatorHook }).__verityAuthEmulator = hook;
}

/**
 * The initialised Auth. Throws if init hasn't run yet — every consumer MUST gate
 * on `useBranding().firebaseReady` before calling this (so the throw is a
 * developer error, never a user-facing one).
 */
export function getFirebaseAuth(): Auth {
  if (!authInstance) {
    throw new Error(
      "Firebase not initialised — gate on useBranding().firebaseReady before getFirebaseAuth()"
    );
  }
  return authInstance;
}

/**
 * The frontend's own build-time (Verity) config — the last-resort fallback used
 * when branding resolution FAILS (state === "fallback") or a partner's config is
 * malformed, so login still works (as Verity). Public values.
 */
export const VERITY_ENV_FIREBASE_CONFIG: FirebaseWebConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};
